import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Contact, Plus } from "lucide-react";
import { HelpdeskPage, useHd } from "./HelpdeskContext";
import { hd, errorMessage } from "./api";
import NewTicketDialog from "./NewTicketDialog";
import { ChannelIcon, PriorityBadge, SmallLabel, StatusBadge } from "./ui";
import { CardHeader, GlassCard, PageHeader, StatRow, StatTile, T, TYPE, formatDateTime, labelSx, primaryButtonSx } from "../user/complaints/shared";

function Profile() {
  const { contactId } = useParams();
  const ctx = useHd();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const load = () => hd.customer(contactId).then(setD).catch((err) => setError(errorMessage(err)));
  useEffect(() => {
    load();
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!d) return <Box sx={{ py: 10, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  const c = d.contact;
  const prefs = d.profile?.notificationPrefs || {};
  const savePref = async (patch) => {
    try {
      await hd.updateCustomerProfile(contactId, patch);
      load();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };

  const th = { "& th": { ...labelSx, bgcolor: T.surfaceHeader } };
  return (
    <Box>
      <PageHeader icon={Contact} title={`${c.firstName} ${c.lastName || ""}`} subtitle={[c.email, c.phone, c.company?.name].filter(Boolean).join(" · ")}
        actions={ctx.can("tickets.create") && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={15} />} onClick={() => setShowNew(true)}>New ticket</Button>} />
      <StatRow columns={5}>
        <StatTile label="Tickets" value={d.stats.totalTickets} />
        <StatTile label="Open tickets" value={d.stats.openTickets} tone={d.stats.openTickets ? "warning" : "good"} />
        <StatTile label="Avg CSAT" value={d.stats.avgCsat ?? "—"} tone="info" />
        <StatTile label="Leads / deals" value={d.deals.length} />
        <StatTile label="Calls" value={d.calls.length} />
      </StatRow>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,2fr) minmax(0,1fr)" }, gap: 2 }}>
        <Box>
          <GlassCard sx={{ mb: 2 }}>
            <CardHeader title="Tickets" subtitle="Across email, chat, WhatsApp and calls" />
            <Table size="small">
              <TableHead><TableRow sx={th}><TableCell>Ticket</TableCell><TableCell>Subject</TableCell><TableCell>Status</TableCell><TableCell>Priority</TableCell><TableCell>Created</TableCell></TableRow></TableHead>
              <TableBody>
                {d.tickets.map((t) => (
                  <TableRow key={t._id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/helpdesk/tickets/${t._id}`)}>
                    <TableCell sx={{ fontWeight: 700, color: T.brand, whiteSpace: "nowrap" }}><ChannelIcon channel={t.channel} /> {t.key}</TableCell>
                    <TableCell>{t.subject}</TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(t.createdAt)}</TableCell>
                  </TableRow>
                ))}
                {!d.tickets.length && <TableRow><TableCell colSpan={5} sx={{ color: T.inkMuted }}>No tickets yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          </GlassCard>
          <GlassCard sx={{ mb: 2 }}>
            <CardHeader title="Leads & deals" />
            <Table size="small">
              <TableBody>
                {d.deals.map((x) => (
                  <TableRow key={x._id}><TableCell>{x.dealName || x.name}</TableCell><TableCell>{x.stage?.name || "—"}</TableCell><TableCell>{x.pipeline}</TableCell><TableCell>{formatDateTime(x.createdAt)}</TableCell></TableRow>
                ))}
                {!d.deals.length && <TableRow><TableCell sx={{ color: T.inkMuted }}>No linked leads</TableCell></TableRow>}
              </TableBody>
            </Table>
          </GlassCard>
          <GlassCard>
            <CardHeader title="Calls" />
            <Table size="small">
              <TableBody>
                {d.calls.map((x) => (
                  <TableRow key={x._id}><TableCell>{x.payload?.status || "call"}</TableCell><TableCell>{x.payload?.agentName || ""}</TableCell><TableCell>{formatDateTime(x.createdAt)}</TableCell><TableCell>{x.blobUrl ? <a href={x.blobUrl} target="_blank" rel="noreferrer">Recording</a> : ""}</TableCell></TableRow>
                ))}
                {!d.calls.length && <TableRow><TableCell sx={{ color: T.inkMuted }}>No calls found for this phone number</TableCell></TableRow>}
              </TableBody>
            </Table>
          </GlassCard>
        </Box>
        <Box>
          <GlassCard sx={{ p: 2, mb: 2 }}>
            <SmallLabel sx={{ mb: 1 }}>Identities</SmallLabel>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>{d.identities.map((i) => <Chip key={i._id} size="small" label={`${i.type}: ${i.value}`} />)}</Stack>
            <SmallLabel sx={{ mt: 2, mb: 0.5 }}>Notification preferences</SmallLabel>
            {["email", "whatsapp", "sms", "push", "chat"].map((k) => (
              <FormControlLabel key={k} sx={{ display: "flex" }} control={<Checkbox size="small" checked={Boolean(prefs[k])} onChange={(e) => savePref({ notificationPrefs: { [k]: e.target.checked } })} disabled={!ctx.can("tickets.update")} />} label={<Typography sx={{ fontSize: TYPE.small, textTransform: "capitalize" }}>{k}</Typography>} />
            ))}
            <SmallLabel sx={{ mt: 2, mb: 0.5 }}>Flags</SmallLabel>
            <FormControlLabel sx={{ display: "flex" }} control={<Checkbox size="small" checked={Boolean(d.profile?.vip)} onChange={(e) => savePref({ vip: e.target.checked })} disabled={!ctx.can("tickets.update")} />} label={<Typography sx={{ fontSize: TYPE.small }}>VIP (AI hands off to a human)</Typography>} />
            <FormControlLabel sx={{ display: "flex" }} control={<Checkbox size="small" checked={Boolean(d.profile?.blocked)} onChange={(e) => savePref({ blocked: e.target.checked })} disabled={!ctx.can("tickets.update")} />} label={<Typography sx={{ fontSize: TYPE.small }}>Blocked (emails/chats rejected)</Typography>} />
          </GlassCard>
          <GlassCard sx={{ p: 2 }}>
            <SmallLabel sx={{ mb: 1 }}>Conversations</SmallLabel>
            {d.conversations.map((x) => (
              <Box key={x._id} sx={{ mb: 1, cursor: "pointer" }} onClick={() => navigate(`/helpdesk/chat?c=${x._id}`)}>
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{x.channel} · {x.status}</Typography>
                <Typography noWrap sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{x.lastMessagePreview}</Typography>
              </Box>
            ))}
            {!d.conversations.length && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>No chats</Typography>}
          </GlassCard>
        </Box>
      </Box>
      {showNew && <NewTicketDialog open defaults={{ contactId }} onClose={() => setShowNew(false)} onCreated={(t) => navigate(`/helpdesk/tickets/${t._id}`)} />}
    </Box>
  );
}

export default function CustomerProfile() {
  return <HelpdeskPage><Profile /></HelpdeskPage>;
}
